import { Component, Inject, OnInit, ViewChild, ElementRef, OnDestroy, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { KitVideoService } from '../../../../shared/services/apis/platform/kit.video.service';
import { VideoStruct } from '../../../../shared/services/structs/book-publish-edit.struct';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export interface PlayerData {
  videos: VideoStruct[];
  currentIndex: number;
  idKit: number;
}

@Component({
  selector: 'app-player-video',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  templateUrl: './player-video.component.html',
  styleUrls: ['./player-video.component.css']
})
export class PlayerVideoComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('videoPlayer') videoPlayer: ElementRef<HTMLVideoElement> | undefined;

  public currentVideo: VideoStruct;
  public safeUrl: SafeResourceUrl | null = null;
  private videos: VideoStruct[];
  private currentIndex: number;
  private idKit: number;

  public showNextVideoOverlay = false;
  public nextVideo: VideoStruct | null = null;
  public countdown: number = 0;

  private watchedVideos: Set<number> = new Set();

  private timeUpdateListener: () => void;
  private endedListener: () => void;
  private countdownInterval: any;

  constructor(
    public dialogRef: MatDialogRef<PlayerVideoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PlayerData,
    private kitVideoService: KitVideoService,
    private sanitizer: DomSanitizer
  ) {
    this.videos = data.videos;
    this.currentIndex = data.currentIndex;
    this.currentVideo = this.videos[this.currentIndex];
    this.idKit = data.idKit;

    this.timeUpdateListener = () => this.onTimeUpdate();
    this.endedListener = () => this.handleVideoEnded();
  }

  ngOnInit(): void {
    this.loadMedia();
  }

  ngAfterViewInit(): void {
    this.setupVideoListeners();
  }

  ngOnDestroy(): void {
    this.removeVideoListeners();
    this.clearCountdownInterval();
  }

  public isPdf(): boolean {
    return this.currentVideo?.videoUrlFile?.toLowerCase().includes('.pdf') ?? false;
  }

  private loadMedia(): void {
    if (this.isPdf()) {
      if (!this.currentVideo.videoUrlFile)
        return;
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.currentVideo.videoUrlFile);
      // Marca o PDF como assistido assim que é carregado
      this.markAsWatched(this.currentVideo.idVideo ?? 0);
    } else {
      this.safeUrl = null; // Limpa a URL segura se não for PDF
    }
  }

  private setupVideoListeners(): void {
    if (this.videoPlayer && this.videoPlayer.nativeElement && !this.isPdf()) {
      this.videoPlayer.nativeElement.addEventListener('timeupdate', this.timeUpdateListener);
      this.videoPlayer.nativeElement.addEventListener('ended', this.endedListener);
    }
  }

  private removeVideoListeners(): void {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      this.videoPlayer.nativeElement.removeEventListener('timeupdate', this.timeUpdateListener);
      this.videoPlayer.nativeElement.removeEventListener('ended', this.endedListener);
    }
  }

  private onTimeUpdate(): void {
    const video = this.videoPlayer?.nativeElement;
    if (video && !this.watchedVideos.has(this.currentVideo.idVideo ?? 0) && !isNaN(video.duration)) {
      const percentageWatched = (video.currentTime / video.duration) * 100;

      if (percentageWatched >= 95) {
        this.markAsWatched(this.currentVideo.idVideo ?? 0);
      }
    }
  }

  private markAsWatched(idVideo: number): void {
    if (this.watchedVideos.has(idVideo)) {
      return;
    }
    this.watchedVideos.add(idVideo);
    this.kitVideoService.post(idVideo).subscribe({
      next: () => console.log(`Item ${idVideo} marcado como assistido.`),
      error: (err) => {
        console.error(`Erro ao marcar item ${idVideo} como assistido`, err);
        this.watchedVideos.delete(idVideo);
      }
    });
  }

  private handleVideoEnded(): void {
    if (this.currentIndex < this.videos.length - 1) {
      this.showNextVideoPrompt();
    } else {
      this.closeDialog();
    }
  }

  private showNextVideoPrompt(): void {
    this.nextVideo = this.videos[this.currentIndex + 1];
    this.showNextVideoOverlay = true;
    this.countdown = 7;

    this.clearCountdownInterval();
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.clearCountdownInterval();
        this.playNext();
      }
    }, 1000);

    if (this.videoPlayer?.nativeElement) {
      this.videoPlayer.nativeElement.pause();
    }
  }

  public playNext(): void {
    this.clearCountdownInterval();
    this.showNextVideoOverlay = false;

    if (this.currentIndex < this.videos.length - 1) {
      this.currentIndex++;
      this.currentVideo = this.videos[this.currentIndex];
      this.nextVideo = null;

      this.removeVideoListeners();
      this.loadMedia();

      // Aguarda o próximo ciclo de detecção de alterações para reconfigurar os listeners
      setTimeout(() => {
        if (this.isPdf()) {
          // Lógica para PDF já tratada em loadMedia
        } else if (this.videoPlayer && this.videoPlayer.nativeElement) {
          this.setupVideoListeners();
          this.videoPlayer.nativeElement.load();
          this.videoPlayer.nativeElement.play();
        }
      });

    } else {
      this.closeDialog();
    }
  }

  public cancelNextVideo(): void {
    this.closeDialog();
  }

  private clearCountdownInterval(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  public closeDialog(): void {
    this.clearCountdownInterval();
    this.dialogRef.close();
  }
}